import {
  format,
  getHours,
  getMilliseconds,
  getMinutes,
  getSeconds,
  parse,
  set,
  startOfMonth,
  startOfQuarter,
  startOfYear,
} from 'date-fns';
import { enUS, ko } from 'date-fns/locale';

import {
  DATE_PICKER_INPUT_CHAR_WIDTH_PX,
  DATE_PICKER_INPUT_DEFAULT_MIN_WIDTH,
  DATE_PICKER_INPUT_HORIZONTAL_PADDING_PX,
} from './constants';

import type { Locale } from 'date-fns';
import type {
  CalendarProps,
  DatePickerProps,
  PeriodPickerKind,
  PeriodPickerProps,
  RangeDatePickerChangeHandler,
  RangeDatePickerProps,
  RangePeriodPickerProps,
  SingleDatePickerChangeHandler,
  SingleDatePickerProps,
} from './types';

export const getLocale = (locale?: 'ko' | 'en'): Locale => {
  return locale === 'ko' ? ko : enUS;
};

/**
 * 타입 가드: Single DatePicker인지 확인합니다.
 */
export const isSingleDatePicker = (props: DatePickerProps): props is SingleDatePickerProps => {
  return !('selectsRange' in props) || props.selectsRange === false;
};

/** DatePicker와 PeriodPicker를 함께 다루기 위한 range 타입 가드 */
export const isRangePicker = (
  props: DatePickerProps | PeriodPickerProps,
): props is RangeDatePickerProps | RangePeriodPickerProps => {
  return props.selectsRange === true;
};

/**
 * period 선택값을 해당 기간의 시작일 00:00:00.000으로 정규화
 */
export const canonicalizePeriodDate = (kind: PeriodPickerKind, date: Date | null): Date | null => {
  if (!date) return null;

  switch (kind) {
    case 'year':
      return startOfYear(date);
    case 'year-month':
      return startOfMonth(date);
    case 'quarter':
      return startOfQuarter(date);
  }
};

/**
 * CalendarProps의 onChange가 RangeDatePickerChangeHandler인지 확인하는 타입 가드
 * selectsRange === true일 때 onChange는 RangeDatePickerChangeHandler입니다.
 */
export const hasRangeOnChange = (
  props: CalendarProps,
): props is CalendarProps & { onChange: RangeDatePickerChangeHandler } => {
  return props.selectsRange === true && props.onChange !== undefined;
};

/**
 * CalendarProps의 onChange가 SingleDatePickerChangeHandler인지 확인하는 타입 가드
 * selectsRange가 false이거나 undefined일 때 onChange는 SingleDatePickerChangeHandler입니다.
 */
export const hasSingleOnChange = (
  props: CalendarProps,
): props is CalendarProps & { onChange: SingleDatePickerChangeHandler } => {
  return props.selectsRange !== true && props.onChange !== undefined;
};

// Re-export getTimezoneOffset from shared utils
export { getTimezoneOffset } from '../../utils/timezone';

/**
 * 날짜를 설정하면서 시간 정보를 보존하거나 정규화합니다.
 *
 * @param date - 설정할 새로운 날짜 (null인 경우 null 반환)
 * @param options - 시간 보존 옵션
 * @param options.showTime - 시간 정보 표시 여부
 * @param options.referenceDate - 시간 정보를 가져올 참조 날짜 (showTime이 true일 때 사용)
 * @returns 시간 정보가 설정된 날짜
 *
 * @example
 * // showTime이 false인 경우: 시간을 00:00:00.000으로 설정
 * setDatePreservingTime(new Date('2024-01-15 14:30:00'), { showTime: false })
 * // => 2024-01-15 00:00:00.000
 *
 * @example
 * // showTime이 true인 경우: referenceDate의 시간 정보를 유지
 * setDatePreservingTime(
 *   new Date('2024-01-15 00:00:00'),
 *   { showTime: true, referenceDate: new Date('2024-01-10 14:30:45') }
 * )
 * // => 2024-01-15 14:30:45.000
 */
export const setDatePreservingTime = (
  date: Date | null,
  options: {
    showTime: boolean;
    referenceDate?: Date | null;
  },
): Date | null => {
  if (!date) return null;

  const { showTime, referenceDate } = options;

  if (showTime && referenceDate) {
    // showTime이 true이고 참조 날짜가 있으면 기존 시간 유지
    return set(date, {
      hours: getHours(referenceDate),
      minutes: getMinutes(referenceDate),
      seconds: getSeconds(referenceDate),
      milliseconds: getMilliseconds(referenceDate),
    });
  }

  // showTime이 false이면 시간을 00:00:00.000으로 설정
  return set(date, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
};

/**
 * 시간을 설정하면서 날짜 정보를 보존합니다.
 *
 * @param time - 설정할 새로운 시간 (null인 경우 null 반환)
 * @param baseDate - 날짜 정보를 가져올 기준 날짜 (undefined인 경우 현재 날짜 사용)
 * @returns 날짜 정보가 보존된 새로운 Date 객체
 *
 * @example
 * // 기준 날짜의 날짜 정보 유지, 새로운 시간으로 설정
 * setTimePreservingDate(
 *   new Date('2024-01-01 14:30:45'),
 *   new Date('2024-06-15 09:00:00')
 * )
 * // => 2024-06-15 14:30:45.000
 *
 * @example
 * // 기준 날짜가 없으면 오늘 날짜 사용
 * setTimePreservingDate(new Date('2024-01-01 14:30:45'))
 * // => 오늘 날짜 14:30:45.000
 */
export const setTimePreservingDate = (time: Date | null, baseDate?: Date | null): Date | null => {
  if (!time) return null;

  const referenceDateForDate = baseDate ?? new Date();

  return set(referenceDateForDate, {
    hours: getHours(time),
    minutes: getMinutes(time),
    seconds: getSeconds(time),
    milliseconds: getMilliseconds(time),
  });
};

/**
 * 입력값이 완전히 입력되었는지 확인합니다.
 *
 * @param value - 입력된 값
 * @param mask - 입력 마스크 (예: "____.__.__")
 * @returns 완전한 입력 여부
 */
export const isCompleteInput = (value: string, mask?: string): boolean => {
  if (!mask) return false;
  const digitsOnly = value.replace(/[^0-9]/g, '');
  const expectedLength = mask.replace(/[^_]/g, '').length;
  return digitsOnly.length >= expectedLength;
};

/**
 * 단일 날짜를 포맷팅하여 문자열로 반환합니다.
 *
 * @param date - 포맷팅할 날짜
 * @param dateFormat - 날짜 포맷 문자열
 * @returns 포맷팅된 날짜 문자열
 */
export const formatSingleDate = (date: Date | null | undefined, dateFormat: string): string => {
  if (!date) return '';
  try {
    return format(date, dateFormat);
  } catch {
    return '';
  }
};

/**
 * 날짜 범위를 포맷팅하여 문자열로 반환합니다.
 *
 * @param startDate - 시작 날짜
 * @param endDate - 종료 날짜
 * @param dateFormat - 날짜 포맷 문자열
 * @returns 포맷팅된 날짜 범위 문자열
 */
export const formatDateRange = (
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
  dateFormat: string,
): string => {
  if (!startDate) return '';
  try {
    if (!endDate) return format(startDate, dateFormat);
    return `${format(startDate, dateFormat)} ~ ${format(endDate, dateFormat)}`;
  } catch {
    return '';
  }
};

/**
 * date-fns parse가 invalid 날짜를 오버플로우(예: "2024.02.31" → 2024.03.02)로
 * 통과시키는 동작을 막기 위해, parse 결과를 다시 format해서 원본 입력과 일치하는지 확인합니다.
 */
const isParsedDateMatchingInput = (parsed: Date, input: string, dateFormat: string): boolean => {
  try {
    return format(parsed, dateFormat) === input;
  } catch {
    return false;
  }
};

/**
 * 단일 날짜 문자열을 파싱합니다.
 *
 * @param value - 파싱할 문자열
 * @param dateFormat - 날짜 포맷 문자열
 * @returns 파싱된 날짜 또는 null. 존재하지 않는 날짜(예: "2024.02.31")는 null 반환
 */
export const parseSingleDate = (value: string, dateFormat: string): Date | null => {
  if (!value) return null;
  try {
    const parsedDate = parse(value, dateFormat, new Date());
    if (isNaN(parsedDate.getTime())) return null;
    if (!isParsedDateMatchingInput(parsedDate, value, dateFormat)) return null;
    return parsedDate;
  } catch {
    return null;
  }
};

/**
 * 입력 문자열에서 dateFormat의 yyyy/MM/dd 토큰 위치를 찾아 범위를 벗어난 숫자를 경계값으로 보정합니다.
 *
 * @description
 * - yyyy(네 자리 년): 0000 → 0001 (date-fns가 0000을 invalid로 처리하므로 라이브러리 최소 유효 년도로 보정)
 * - MM(두 자리 월): 13~99 → 12, 0 → 1
 * - dd(두 자리 일): 32~99 → 31, 0 → 1
 * - 해당 월에 존재하지 않는 일(예: 2.31)은 보정 대상이 아님 (parseSingleDate 단계에서 invalid 처리)
 * - 토큰 위치의 자리가 모두 숫자가 아니면 해당 토큰은 건너뜀
 * - 한 자리 토큰(y/M/d), yy, 시간 토큰은 손대지 않음
 *
 * @param input - 입력 문자열 (예: "2026.13.05")
 * @param dateFormat - 날짜 포맷 문자열 (예: "yyyy.MM.dd")
 * @returns 보정된 문자열. 보정할 부분이 없으면 입력을 그대로 반환
 */
export const clampDateInputParts = (input: string, dateFormat: string): string => {
  if (input.length !== dateFormat.length) return input;

  const result = input.split('');
  let i = 0;
  while (i < dateFormat.length) {
    const isYYYY =
      dateFormat[i] === 'y' &&
      dateFormat[i + 1] === 'y' &&
      dateFormat[i + 2] === 'y' &&
      dateFormat[i + 3] === 'y' &&
      dateFormat[i + 4] !== 'y';
    const isMM = dateFormat[i] === 'M' && dateFormat[i + 1] === 'M' && dateFormat[i + 2] !== 'M';
    const isDD = dateFormat[i] === 'd' && dateFormat[i + 1] === 'd';

    if (isYYYY) {
      const part = input.slice(i, i + 4);
      if (/^\d{4}$/.test(part) && part === '0000') {
        result[i] = '0';
        result[i + 1] = '0';
        result[i + 2] = '0';
        result[i + 3] = '1';
      }
      i += 4;
      continue;
    }

    if (!isMM && !isDD) {
      i += 1;
      continue;
    }
    const part = input.slice(i, i + 2);
    if (/^\d{2}$/.test(part)) {
      const value = Number(part);
      const max = isMM ? 12 : 31;
      const clamped = value < 1 ? 1 : value > max ? max : value;
      if (clamped !== value) {
        const padded = String(clamped).padStart(2, '0');
        result[i] = padded[0];
        result[i + 1] = padded[1];
      }
    }
    i += 2;
  }
  return result.join('');
};

/**
 * Date 를 [minDate, maxDate] 경계로 보정합니다.
 *
 * @description
 * - `date < minDate` → minDate
 * - `date > maxDate` → maxDate
 * - 그 외에는 원본 Date 를 그대로 반환 (참조 동일)
 * - minDate / maxDate 가 null·undefined 이면 해당 경계는 무시
 *
 * @param date - 검사할 날짜
 * @param minDate - 최소 경계 (선택)
 * @param maxDate - 최대 경계 (선택)
 * @returns 경계로 보정된 Date 또는 원본 Date
 */
export const clampDateToBounds = (
  date: Date,
  minDate?: Date | null,
  maxDate?: Date | null,
): Date => {
  if (minDate && date < minDate) return minDate;
  if (maxDate && date > maxDate) return maxDate;
  return date;
};

/**
 * 날짜 범위 문자열을 파싱합니다.
 *
 * @param value - 파싱할 문자열 (예: "2024.01.01 ~ 2024.01.31")
 * @param dateFormat - 날짜 포맷 문자열
 * @returns 파싱된 날짜 범위 [startDate, endDate] 또는 [null, null]. 존재하지 않는 날짜는 [null, null]
 */
export const parseDateRange = (value: string, dateFormat: string): [Date | null, Date | null] => {
  if (!value) return [null, null];
  try {
    const parts = value.split(' ~ ');
    if (parts.length !== 2) return [null, null];

    const startInput = parts[0].trim();
    const endInput = parts[1].trim();
    const startDate = parse(startInput, dateFormat, new Date());
    const endDate = parse(endInput, dateFormat, new Date());

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return [null, null];
    }
    if (
      !isParsedDateMatchingInput(startDate, startInput, dateFormat) ||
      !isParsedDateMatchingInput(endDate, endInput, dateFormat)
    ) {
      return [null, null];
    }

    if (startDate > endDate) {
      return [endDate, startDate];
    }

    return [startDate, endDate];
  } catch {
    return [null, null];
  }
};

/**
 * monthsShown과 showTime의 호환성을 검증하고 최종 showTime 값을 반환합니다.
 *
 * @param monthsShown - 표시할 개월 수
 * @param showTime - 시간 선택 표시 여부
 * @returns 검증된 showTime 값
 *
 * @example
 * validateCalendarProps(1, true) // => true (정상)
 * validateCalendarProps(2, true) // => false (경고 출력)
 */
export const validateCalendarProps = (monthsShown: number, showTime: boolean): boolean => {
  const isMultipleMonths = monthsShown !== 1;
  const shouldShowTime = isMultipleMonths ? false : showTime;

  if (process.env.NODE_ENV === 'development' && isMultipleMonths && showTime) {
    console.error(
      '[Calendar] showTime은 monthsShown이 1일 때만 사용할 수 있습니다. monthsShown이 1이 아닐 때는 showTime이 자동으로 false로 설정됩니다.',
    );
  }

  return shouldShowTime;
};

/**
 * Portal 마운트 타이밍 이슈를 체크합니다.
 * target이 없는 경우(마운트 전 등) true를 반환합니다.
 *
 * @description
 * document.body 체크는 제거함. Modal/Portal 환경에서 클릭 시 target이 body로
 * 올 수 있어, 이 경우에도 캘린더가 닫혀야 하므로 무시하지 않음.
 *
 * @param target - 체크할 HTMLElement
 * @returns Portal 마운트 타이밍 이슈 여부
 */
export const isPortalMountingIssue = (target: HTMLElement | null | undefined): boolean => {
  return !target;
};

/**
 * MUI Backdrop 요소인지 체크합니다.
 *
 * @param target - 체크할 HTMLElement
 * @returns MUI Backdrop 여부
 */
export const isMuiBackdrop = (target: HTMLElement): boolean => {
  return target.classList.contains('MuiBackdrop-root');
};

/**
 * target이 element 내부에 있는지 체크합니다.
 *
 * @param target - 체크할 HTMLElement
 * @param element - 기준 HTMLElement
 * @returns element 내부 포함 여부
 */
export const isWithinElement = (
  target: HTMLElement,
  element: HTMLElement | null | undefined,
): boolean => {
  return !!element && element.contains(target);
};

/**
 * react-datepicker 캘린더 요소인지 체크합니다.
 *
 * @param target - 체크할 HTMLElement
 * @returns react-datepicker 캘린더 여부
 */
export const isDatePickerCalendar = (target: HTMLElement): boolean => {
  return !!target.closest('.react-datepicker, .react-datepicker-popper');
};

/**
 * react-datepicker-ignore-onclickoutside 클래스를 가진 요소인지 체크합니다.
 *
 * @param target - 체크할 HTMLElement
 * @returns ignore 요소 여부
 */
export const isIgnoredElement = (target: HTMLElement): boolean => {
  return (
    !!target.closest('.react-datepicker-ignore-onclickoutside') ||
    !!target.closest('.react-datepicker__day--outside-month')
  );
};

/**
 * MUI Select 드롭다운 요소인지 체크합니다.
 * TimePicker의 Select처럼 Menu/Popover 내부의 Paper·List만 해당하며,
 * Modal 내부의 Paper(모달 본문)는 제외합니다.
 *
 * @param target - 체크할 HTMLElement
 * @returns Select 드롭다운 요소 또는 null
 */
export const findSelectDropdown = (target: HTMLElement): Element | null => {
  const el = target.closest(
    '.MuiPaper-root, .MuiList-root, .MuiMenuItem-root, [role="listbox"], [role="option"]',
  );
  if (!el) return null;
  // Modal 본문(MuiPaper)이 아닌, Menu/Popover 내부의 드롭다운일 때만 무시
  return el.closest('.MuiMenu-root, .MuiPopover-root') ? el : null;
};

/**
 * dateFormat 문자열로부터 mask 패턴을 자동 생성합니다.
 * date-fns 포맷 토큰을 입력 마스크 문자('_')로 변환합니다.
 *
 * @param dateFormat - date-fns 포맷 문자열 (예: "yyyy.MM.dd HH:mm:ss")
 * @returns 생성된 mask 문자열 (예: "____.__.__ __:__:__")
 *
 * @example
 * generateMaskFromDateFormat("yyyy.MM.dd") // "____.__.__ "
 * generateMaskFromDateFormat("yyyy.MM.dd HH:mm:ss") // "____.__.__ __:__:__"
 * generateMaskFromDateFormat("yyyy.MM.dd 23:59:59") // "____.__.__ 23:59:59" (literal 유지)
 */
/**
 * mask 길이 기반으로 DatePickerInput의 min-width를 동적으로 계산합니다.
 *
 * @param mask - 입력 마스크 (예: "____.__.__ __:__:__ ~ ____.__.__ __:__:__")
 * @returns 계산된 min-width (px)
 *
 * @example
 * calcDatePickerInputMinWidth() // 240 (mask 없을 때 기본값)
 * calcDatePickerInputMinWidth("____.__.__") // 240 (10자, 기본값 floor)
 * calcDatePickerInputMinWidth("____.__.__ __:__:__ ~ ____.__.__ __:__:__") // 364 (41자)
 */
export const calcDatePickerInputMinWidth = (mask?: string): number => {
  if (!mask) return DATE_PICKER_INPUT_DEFAULT_MIN_WIDTH;
  return Math.max(
    DATE_PICKER_INPUT_DEFAULT_MIN_WIDTH,
    Math.ceil(mask.length * DATE_PICKER_INPUT_CHAR_WIDTH_PX) +
      DATE_PICKER_INPUT_HORIZONTAL_PADDING_PX,
  );
};

export const generateMaskFromDateFormat = (dateFormat: string): string => {
  return dateFormat
    .replace(/yyyy/g, '____')
    .replace(/yy/g, '__')
    .replace(/MMMM/g, '____')
    .replace(/MMM/g, '___')
    .replace(/MM/g, '__')
    .replace(/M/g, '_')
    .replace(/dd/g, '__')
    .replace(/d/g, '_')
    .replace(/HH/g, '__')
    .replace(/H/g, '_')
    .replace(/hh/g, '__')
    .replace(/h/g, '_')
    .replace(/mm/g, '__')
    .replace(/m/g, '_')
    .replace(/ss/g, '__')
    .replace(/s/g, '_')
    .replace(/SSS/g, '___')
    .replace(/SS/g, '__')
    .replace(/S/g, '_')
    .replace(/a/g, '__');
};
